import React, { useState, useEffect } from "react";
import { Form, Input, Button, Card, Typography, Spin, Alert, Checkbox, Image, message } from "antd";
import { UserOutlined, LockOutlined, SafetyOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { getCaptcha } from "../../api/auth";
import { API_BASE_URL } from "../../api/config";
import "./LoginPage.css";

const { Title } = Typography;

/**
 * 登录页面组件
 */
const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, loading, error } = useAuth();
    const [form] = Form.useForm();

    // 验证码状态
    const [captcha, setCaptcha] = useState({
        id: "",
        image: "",
        loading: false,
        error: false,
    });
    const [captchaRequired, setCaptchaRequired] = useState(false);

    // 获取重定向路径
    const from = location.state?.from || "/";

    const clearCaptchaChallenge = () => {
        setCaptchaRequired(false);
        setCaptcha({
            id: "",
            image: "",
            loading: false,
            error: false,
        });
        form.setFieldsValue({ captcha: "" });
    };

    // 渲染验证码部分
    const renderCaptchaImage = () => {
        if (captcha.loading) {
            return <Spin size="small" />;
        }

        if (captcha.error || !captcha.image) {
            return (
                <Button type="default" onClick={fetchCaptcha} icon={<ReloadOutlined />} style={{ height: 32 }}>
                    点击获取验证码
                </Button>
            );
        }

        return (
            <Image
                src={captcha.image}
                alt="验证码"
                height={32}
                preview={false}
                style={{ cursor: "pointer" }}
                onClick={fetchCaptcha}
                onError={() => {
                    setCaptcha((prev) => ({ ...prev, error: true }));
                    message.error("验证码图片加载失败，请点击重试");
                }}
            />
        );
    };

    // 获取验证码
    const fetchCaptcha = async () => {
        try {
            setCaptcha((prev) => ({ ...prev, loading: true, error: false }));
            const data = await getCaptcha();
            setCaptcha({
                id: data.captcha_id,
                image: data.captcha_image,
                loading: false,
                error: false,
            });
        } catch (error) {
            console.error("获取验证码失败:", {
                status: error.response?.status,
                code: error.response?.data?.detail?.code || error.code,
            });
            message.error(`获取验证码失败: ${error.message || "未知错误"}`);
            setCaptcha((prev) => ({
                ...prev,
                loading: false,
                error: true,
            }));
        }
    };

    // 初始化时不获取验证码；后端风险态返回 captcha_required 后再展示和获取。
    useEffect(() => {
        // 添加诊断信息输出
        console.log("环境信息:", {
            hostname: window.location.hostname,
            apiBaseUrl: API_BASE_URL || "未定义",
            protocol: window.location.protocol,
            userAgent: navigator.userAgent,
            platform: navigator.platform,
        });

        // 检查网络连接状态
        const checkNetworkStatus = () => {
            console.log("网络状态:", navigator.onLine ? "在线" : "离线");
        };

        // 初始检查
        checkNetworkStatus();

        // 添加网络状态变化监听
        window.addEventListener("online", checkNetworkStatus);
        window.addEventListener("offline", checkNetworkStatus);

        return () => {
            window.removeEventListener("online", checkNetworkStatus);
            window.removeEventListener("offline", checkNetworkStatus);
        };
    }, []);

    // 表单提交处理
    const handleSubmit = async (values) => {
        const loginData = {
            username: values.username,
            password: values.password,
            remember: values.remember || false,
            captchaId: captchaRequired ? captcha.id : undefined,
            captchaCode: captchaRequired ? values.captcha : undefined,
        };

        const result = await login(loginData);
        if (result?.ok) {
            clearCaptchaChallenge();
            navigate(from, { replace: true });
        } else if (result?.captchaRequired) {
            setCaptchaRequired(true);
            form.setFieldsValue({ captcha: "" });
            await fetchCaptcha();
        }
    };

    return (
        <div
            className="login-page"
            style={{
                backgroundImage: "url('/pic/login-background.webp')",
            }}
        >
            <div className="login-page__overlay" />

            <div className="login-page__panel">
                <Card variant="borderless" className="login-page__card">
                    <div className="login-page__header">
                        <Title level={1} className="login-page__title">
                            Fly Meta
                        </Title>
                    </div>

                    {error && <Alert message={error} type="error" showIcon className="login-page__alert" />}

                    <Spin spinning={loading}>
                        <Form form={form} name="login" onFinish={handleSubmit} autoComplete="off" size="large" layout="vertical">
                            <Form.Item name="username" rules={[{ required: true, message: "请输入用户名" }]}>
                                <Input
                                    prefix={<UserOutlined style={{ color: "#134e8e", opacity: 0.8 }} />}
                                    placeholder="用户名"
                                    onChange={clearCaptchaChallenge}
                                    className="login-page__input"
                                />
                            </Form.Item>

                            <Form.Item name="password" rules={[{ required: true, message: "请输入密码" }]}>
                                <Input.Password
                                    prefix={<LockOutlined style={{ color: "#134e8e", opacity: 0.8 }} />}
                                    placeholder="密码"
                                    className="login-page__input"
                                />
                            </Form.Item>

                            {captchaRequired && (
                                <Form.Item name="captcha" rules={[{ required: true, message: "请输入验证码" }]}>
                                    <div className="login-page__captcha">
                                        <Input
                                            prefix={<SafetyOutlined style={{ color: "#134e8e", opacity: 0.8 }} />}
                                            placeholder="验证码"
                                            className="login-page__input login-page__captcha-input"
                                        />
                                        <div className="login-page__captcha-image">
                                            {renderCaptchaImage()}
                                            <Button icon={<ReloadOutlined />} onClick={fetchCaptcha} loading={captcha.loading} type="text" style={{ marginLeft: 4 }} />
                                        </div>
                                    </div>
                                </Form.Item>
                            )}

                            <Form.Item>
                                <div className="login-page__meta">
                                    <Form.Item name="remember" valuePropName="checked" initialValue={true} noStyle>
                                        <Checkbox className="login-page__remember">记住我</Checkbox>
                                    </Form.Item>
                                    <a href="#" className="login-page__link">
                                        忘记密码？
                                    </a>
                                </div>
                            </Form.Item>

                            <Form.Item style={{ marginBottom: 10 }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    className="login-page__submit"
                                >
                                    登 录
                                </Button>
                            </Form.Item>
                        </Form>
                    </Spin>
                </Card>

            </div>
        </div>
    );
};

export default LoginPage;
